/** User-facing description of the backend password policy (8+, upper, lower, digit). */
export const PASSWORD_HELP = 'Use 8+ characters with uppercase, lowercase, and numbers';

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Tailwind background class (semantic token) for the strength bar fill. */
  barClass: string;
}

const STRENGTH_BY_SCORE: Record<0 | 1 | 2 | 3 | 4, Omit<PasswordStrength, 'score'>> = {
  0: { label: 'Very weak', barClass: 'bg-error-500' },
  1: { label: 'Weak', barClass: 'bg-error-500' },
  2: { label: 'Fair', barClass: 'bg-warning-500' },
  3: { label: 'Strong', barClass: 'bg-success-500' },
  4: { label: 'Very strong', barClass: 'bg-success-600' },
};

/** Heuristic password strength on a 0–4 scale, for the strength meter. */
export const computePasswordStrength = (password: string): PasswordStrength => {
  if (!password) return { score: 0, ...STRENGTH_BY_SCORE[0] };

  let raw = 0;
  if (password.length >= 8) raw++;
  if (password.length >= 12) raw++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) raw++;
  if (/\d/.test(password)) raw++;
  if (/[^a-zA-Z0-9]/.test(password)) raw++;

  const score = Math.min(raw, 4) as 0 | 1 | 2 | 3 | 4;
  return { score, ...STRENGTH_BY_SCORE[score] };
};

export interface PasswordValidation {
  valid: boolean;
  errors: string[];
}

/**
 * Validate a new password against the backend policy (mirrors
 * `app/api/routes/auth.py`): 8+ chars, lowercase, uppercase, a digit, and
 * matching confirmation.
 */
export const validateNewPassword = (
  newPassword: string,
  confirmPassword: string
): PasswordValidation => {
  const errors: string[] = [];
  if (newPassword.length < 8) errors.push('Password must be at least 8 characters');
  if (!/[a-z]/.test(newPassword)) errors.push('Password must contain lowercase letters');
  if (!/[A-Z]/.test(newPassword)) errors.push('Password must contain uppercase letters');
  if (!/\d/.test(newPassword)) errors.push('Password must contain numbers');
  if (newPassword !== confirmPassword) errors.push('Passwords do not match');
  return { valid: errors.length === 0, errors };
};
